'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import LogoPhiStatic from '@/components/logos/LogoPhiStatic';
import { useAuth } from '@/hooks/useAuth';

type EnvironmentType = 'production' | 'preproduction' | 'development' | null;

interface FooterConfig {
  showDocs: boolean;
}

export default function Footer() {
  const [environment, setEnvironment] = useState<EnvironmentType>(null);
  const [config, setConfig] = useState<FooterConfig>({ showDocs: false });
  const { user } = useAuth();

  useEffect(() => {
    // Get environment from Next.js public environment variable
    // This is set in docker-compose.yaml for each service
    const env = process.env.NEXT_PUBLIC_ENVIRONMENT as EnvironmentType || 'production';
    setEnvironment(env);

    // Fetch config for docs visibility
    fetch('/api/config/get?key=SHOW_DOCS')
      .then(res => res.json())
      .then(data => {
        setConfig({
          showDocs: data.success && (data.value === true || data.value === 'true')
        });
      })
      .catch(err => console.error('[Footer] Failed to fetch config:', err));
  }, []);

  // Different styling for different environments
  const isPreprod = environment === 'preproduction';
  const bgGradient = isPreprod
    ? 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600'
    : 'bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600';

  const title = isPreprod ? '🔵 PRE-PRODUCTION ENVIRONMENT' : '⚠️ DEVELOPMENT ENVIRONMENT';
  const subtitle = isPreprod
    ? 'Testing Environment • Isolated Data • Production-Like Configuration'
    : 'Test Mode • Stripe Test Keys Active • Not for Production Use';

  return (
    <footer className="bg-brand-navy border-t border-brand-blue/20 text-white">
      {/* Environment Warning Banner */}
      {environment && environment !== 'production' && (
        <div className={`${bgGradient} text-white py-3 px-6`}>
          <div className="container mx-auto text-center">
            <p className="text-sm font-semibold">
              {title}
            </p>
            <p className="text-xs opacity-90 mt-1">
              {subtitle}
            </p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-6 py-12">
        <div className={`grid grid-cols-1 gap-8 ${config.showDocs ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          {/* Brand */}
          <div className="col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <LogoPhiStatic className="w-12 h-12" />
              <div>
                <h3 className="text-lg font-montserrat font-bold">
                  ΘDTE <span className="text-brand-orange">Toolbox</span>
                </h3>
                <p className="text-xs text-gray-400">Real-Time Options Analytics</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 mt-4">
              Your Edge in Options Trading
            </p>
            <p className="text-xs text-gray-500 mt-2">
              info@0dte-toolbox.com
            </p>
            {user && (
              <div className="mt-4 pt-4 border-t border-brand-blue/20">
                <p className="text-xs text-gray-500">Logged in as:</p>
                <p className="text-sm text-gray-300 truncate">{user.email}</p>
                <p className="text-xs text-brand-orange font-semibold mt-1">
                  {user.subscription_title || 'Basic'} (Tier {user.tier})
                </p>
              </div>
            )}
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              {/* <li><Link href="/services" className="hover:text-brand-blue transition-colors">Services</Link></li> */}
              <li><Link href="/pricing" className="hover:text-brand-blue transition-colors">Pricing</Link></li>
              <li><Link href="/about" className="hover:text-brand-blue transition-colors">About</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link href="/blog" className="hover:text-brand-blue transition-colors">Blog</Link></li>
              <li><Link href="/contact" className="hover:text-brand-blue transition-colors">Contact</Link></li>
            </ul>
          </div>

          {config.showDocs && (
            <div>
              <h4 className="font-semibold mb-4">Documentation</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a 
                    href="https://asdkj.0dte-toolbox.com/docs" 
                    className="hover:text-brand-blue transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    User Guides
                  </a>
                </li>
              </ul>
            </div>
          )}

          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link href="/privacy" className="hover:text-brand-blue transition-colors">Privacy Policy</Link></li>
              <li><Link href="/terms" className="hover:text-brand-blue transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-brand-blue/20 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400 mb-6">
            <p>&copy; {new Date().getFullYear()} ΘDTE Toolbox. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="#" className="hover:text-brand-blue transition-colors">Twitter</a>
              <a href="#" className="hover:text-brand-blue transition-colors">LinkedIn</a>
            </div>
          </div>

          <div className="text-xs text-gray-500 text-justify">
            <p className="font-semibold mb-1">Disclaimer:</p>
            <p>
              This dashboard is for informational and educational purposes only. It is not intended as investment advice,
              a recommendation, or an offer to buy or sell any securities. All data presented should be independently
              verified by the user. Users are solely responsible for their own investment decisions and should consult
              a qualified financial advisor before making any trading or investment decisions.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
